import { Component, EventEmitter, Input, Output } from '@angular/core';
import { IBloquePlantilla, IPlantillaBotonera } from 'modelos/src';
import {
  esItemEspecial,
  FONDO_TEMA,
  PANTALLAS,
  pantallasDe,
} from '../../../../auxiliares/plantillas/plantilla-manual';
import {
  errorApp,
  primarioApp,
} from '../../../../auxiliares/plantillas/colores-app';
import {
  brandInk,
  PreviewBoton,
  PreviewVM,
} from '../../provisionador/preview/preview-vm';

/** Ancho de la pantalla del preview en px (300 del marco − 2×11 de bisel). El nativo diseña sobre ~360dp. */
const ANCHO_PX = 278;
const ESCALA = ANCHO_PX / 360;

/**
 * Imágenes por defecto de la fila: LAS MISMAS que usa el nativo cuando el cliente no cargó las suyas
 * (`InicioViewModel`: DEF_REFLECTOR / DEF_SIRENA / DEF_ADELANTE / DEF_ATRAS).
 */
const IMG_DEFECTO: Record<string, string> = {
  reflector: 'https://storage.googleapis.com/novit/botones/sirena-luz.png',
  sirena: 'https://storage.googleapis.com/novit/botones/sirena-sonido.png',
  mas: 'https://storage.googleapis.com/novit/botones/mas.png',
  atras: 'https://storage.googleapis.com/novit/botones/izquierda.png',
};

/** Una celda de fila ya resuelta: botón de la categoría o pieza fija. */
interface CeldaFila {
  item: string;
  boton?: PreviewBoton;
  /** "Más" hacia una pantalla todavía vacía: se muestra atenuado (en la app no aparece). */
  vacia?: boolean;
}

/**
 * Preview en vivo de la plantilla manual, una pantalla a la vez. Espeja `PlantillaManualTemplate.kt`
 * del nativo: las piezas en columna, la que "ocupa el espacio libre" se estira. "Más" y "Atrás"
 * navegan entre pantallas igual que en la app. Es una aproximación (HTML/CSS), no el render real.
 */
@Component({
  selector: 'app-plantilla-manual-preview',
  templateUrl: './plantilla-manual-preview.component.html',
  styleUrls: ['./plantilla-manual-preview.component.scss'],
  standalone: false,
})
export class PlantillaManualPreviewComponent {
  @Input() plantilla?: IPlantillaBotonera | null;
  @Input() vm!: PreviewVM;
  /** Botones de la categoría por `_tmpId` (los que referencian las piezas). */
  @Input() botones: Record<string, PreviewBoton> = {};
  @Input() pantalla = 0;
  @Output() pantallaChange = new EventEmitter<number>();

  readonly info = PANTALLAS;

  get bloques(): IBloquePlantilla[] {
    return pantallasDe(this.plantilla)[this.pantalla]?.bloques ?? [];
  }

  private tienePiezas(i: number): boolean {
    return !!pantallasDe(this.plantilla)[i]?.bloques?.length;
  }

  ir(i: number): void {
    this.pantallaChange.emit(i);
  }

  px(dp?: number): number {
    return Math.round((dp ?? 0) * ESCALA);
  }

  /** URL de la imagen de la pieza. Sin cargar = `undefined`: la app no dibuja nada (sin ejemplo). */
  imagen(b: IBloquePlantilla): string | undefined {
    return (b.imagen === 'abajo' ? this.vm?.abajo : this.vm?.arriba) || undefined;
  }

  /** Imagen de una pieza fija de la fila: la del cliente/categoría o la por defecto del nativo. */
  imagenItem(item: string): string {
    const propias: Record<string, string | undefined> = {
      reflector: this.vm?.reflector,
      sirena: this.vm?.sirena,
      mas: this.vm?.adelante,
      atras: this.vm?.atras,
    };
    return propias[item] || IMG_DEFECTO[item];
  }

  /**
   * "Color del tema" como lo pinta la app: el `primary` del esquema Material 3 que arma desde el
   * `colorTema` (no el `colorTema` crudo: el naranja #FB972E se ve marrón #87521c en el teléfono).
   */
  get colorPrimario(): string {
    return primarioApp(this.vm?.brand);
  }

  /** Fondo de los botones sin color propio, como en la app (`colorScheme.error`). */
  get colorError(): string {
    return errorApp(this.vm?.brand);
  }

  /** Franja de color detrás de una imagen: el del tema, uno elegido o ninguna. */
  fondoImagen(b: IBloquePlantilla): string | null {
    if (!b.colorFondo) return null;
    return b.colorFondo === FONDO_TEMA ? this.colorPrimario : b.colorFondo;
  }

  /** Botones elegidos en la pieza que existen (uno quitado de la categoría no se dibuja). */
  botonesDe(b: IBloquePlantilla): PreviewBoton[] {
    return (b.idsBotones ?? [])
      .map((id) => this.botones[id])
      .filter((x): x is PreviewBoton => !!x);
  }

  /** Filas de la grilla de botones (de a `columnas`). */
  filas(b: IBloquePlantilla): PreviewBoton[][] {
    const cols = Math.min(3, Math.max(1, b.columnas ?? 1));
    const lista = this.botonesDe(b);
    const out: PreviewBoton[][] = [];
    for (let i = 0; i < lista.length; i += cols) out.push(lista.slice(i, i + cols));
    return out;
  }

  /** Alto de cada botón: el elegido, o la regla por cantidad de filas del nativo (techo). */
  altoBoton(b: IBloquePlantilla): number {
    if (b.alto) return this.px(b.alto);
    const filas = this.filas(b).length;
    return this.px(filas <= 1 ? 200 : filas <= 3 ? 120 : 88);
  }

  /** Celdas de la fila que el nativo realmente dibuja con los datos actuales. */
  celdas(b: IBloquePlantilla): CeldaFila[] {
    const out: CeldaFila[] = [];
    for (const item of b.items ?? []) {
      if (!esItemEspecial(item)) {
        const boton = this.botones[item];
        if (boton) out.push({ item, boton });
        continue;
      }
      if ((item === 'reflector' || item === 'sirena') && !this.vm?.sirenas) continue;
      // "Más" se muestra aunque la pantalla siguiente esté vacía (atenuado), así se puede ir a
      // armarla. En la app sí se esconde hasta que esa pantalla tenga piezas.
      if (item === 'mas') {
        if (this.pantalla >= 2) continue;
        out.push({ item, vacia: !this.tienePiezas(this.pantalla + 1) });
        continue;
      }
      if (item === 'atras' && this.pantalla === 0) continue;
      out.push({ item });
    }
    return out;
  }

  click(c: CeldaFila): void {
    if (c.item === 'mas') this.ir(this.pantalla + 1);
    if (c.item === 'atras') this.ir(this.pantalla - 1);
  }

  readonly ITEM: Record<string, { ic: string; color: string; l: string }> = {
    reflector: { ic: '💡', color: '#475467', l: 'Reflector' },
    sirena: { ic: '🔊', color: '#b42318', l: 'Sirena' },
    mas: { ic: 'Más', color: '#e5e7eb', l: 'Más' },
    atras: { ic: 'Atrás', color: '#e5e7eb', l: 'Atrás' },
  };

  ink(c?: string): string {
    return brandInk(c);
  }
}
